import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ContaContabilSearchCombobox } from "@/components/ContaContabilSearchCombobox";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { usePaginacaoServidor } from "@/hooks/usePaginacaoServidor";
import { useDebounce } from "@/hooks/useDebounce";
import { useEstornarLancamento, usePartidas } from "@/features/contabil/hooks/useContabilizacao";
import {
  TIPO_OPERACAO_LABEL, type LancamentoPartida, type TipoOperacaoContabil,
} from "@/features/contabil/types/contabilizacao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const hoje = () => new Date().toISOString().slice(0, 10);
const inicioMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const ORIGENS = Object.entries(TIPO_OPERACAO_LABEL) as [string, string][];

export default function LancamentosContabeisPage() {
  const [dtInicio, setDtInicio] = useState(inicioMes());
  const [dtFim, setDtFim] = useState(hoje());
  const [contaId, setContaId] = useState<number | null>(null);
  const [origem, setOrigem] = useState("__todas__");
  const [numero, setNumero] = useState("");
  const numeroDeb = useDebounce(numero.trim(), 400);

  const pag = usePaginacaoServidor({ resetarQuando: [dtInicio, dtFim, contaId, origem, numeroDeb] });
  const { data, isLoading, isFetching } = usePartidas({
    dtInicio: dtInicio || undefined,
    dtFim: dtFim || undefined,
    contaId: contaId ?? undefined,
    tipoOperacao: origem === "__todas__" ? undefined : Number(origem),
    numero: /^\d+$/.test(numeroDeb) ? Number(numeroDeb) : undefined,
    pagina: pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
  });

  const estornar = useEstornarLancamento();
  const [alvo, setAlvo] = useState<LancamentoPartida | null>(null);
  const [motivo, setMotivo] = useState("");

  const confirmarEstorno = async () => {
    if (!alvo) return;
    await estornar.mutateAsync({ num: alvo.numeroLancamento, motivo: motivo.trim() });
    setAlvo(null);
    setMotivo("");
  };

  const itens = data?.itens ?? [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Lançamentos Contábeis</h1>
          <p className="text-muted-foreground">
            Partidas dobradas geradas pelas operações e pelos lançamentos manuais.
          </p>
        </div>
        <Button asChild>
          <Link to="/contabil/lancamentos/novo"><Plus className="h-4 w-4 mr-2" />Lançamento manual</Link>
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <Label>De</Label>
            <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
          </div>
          <div>
            <Label>Até</Label>
            <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Conta (débito ou crédito)</Label>
            <ContaContabilSearchCombobox
              value={contaId}
              onChange={setContaId}
              placeholder="Todas as contas"
            />
          </div>
          <div>
            <Label>Origem</Label>
            <Select value={origem} onValueChange={setOrigem}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todas__">Todas</SelectItem>
                {ORIGENS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nº lançamento</Label>
            <Input inputMode="numeric" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Ex.: 1234" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? <Skeleton className="h-48" /> : (
            <>
              <Table className={isFetching ? "opacity-60" : undefined}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Nº</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Pessoa</TableHead>
                    <TableHead>Débito</TableHead>
                    <TableHead>Crédito</TableHead>
                    <TableHead>Histórico</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      Nenhum lançamento encontrado com esses filtros.
                    </TableCell></TableRow>
                  ) : itens.map((l) => (
                    <TableRow key={`${l.numeroLancamento}-${l.tipoOperacao}-${l.operacaoId}`}>
                      <TableCell>{new Date(l.dtMovimento).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</TableCell>
                      <TableCell className="font-mono">{l.numeroLancamento}</TableCell>
                      <TableCell className="text-xs">
                        {TIPO_OPERACAO_LABEL[l.tipoOperacao as TipoOperacaoContabil] ?? "—"}
                        {l.estornado && <Badge variant="outline" className="ml-2 text-red-600">Estornado</Badge>}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{l.documento || "—"}</TableCell>
                      <TableCell className="text-xs">{l.pessoa || "—"}</TableCell>
                      <TableCell className="text-xs">{l.contasDebito.join("; ") || "—"}</TableCell>
                      <TableCell className="text-xs">{l.contasCredito.join("; ") || "—"}</TableCell>
                      <TableCell className="text-xs max-w-[16rem] truncate" title={l.historico ?? undefined}>
                        {l.historico || "—"}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{fmtCur(l.valor)}</TableCell>
                      <TableCell>
                        {l.tipoOperacao !== 99 && !l.estornado && (
                          <Button size="sm" variant="ghost" onClick={() => setAlvo(l)} title="Estornar">
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginacaoRodape {...pag.rodape(data)} />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!alvo} onOpenChange={(o) => { if (!o) { setAlvo(null); setMotivo(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Estornar lançamento nº {alvo?.numeroLancamento}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Gera o contra-lançamento (débito ↔ crédito) de todas as partidas desta operação
            ({alvo ? TIPO_OPERACAO_LABEL[alvo.tipoOperacao as TipoOperacaoContabil] : ""},
            {alvo ? ` ${fmtCur(alvo.valor)}` : ""}).
          </p>
          <div>
            <Label>Motivo</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlvo(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={confirmarEstorno}
              disabled={motivo.trim().length < 3 || estornar.isPending}
            >
              {estornar.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Estornar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
